import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Landmark } from 'lucide-react'

import { formatMoney } from '@/lib/format'
import type { PnL, PnLLine } from '@/types/finance'

interface PnLStatementProps {
  pnl?: PnL
  priorPnl?: PnL
  isLoading: boolean
}

function SectionRows({
  lines,
  priorByCategory,
}: {
  lines: PnLLine[]
  priorByCategory: Map<string, number>
}) {
  if (lines.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={3} className="py-6 text-center text-sm text-muted-foreground">
          No entries in this period.
        </TableCell>
      </TableRow>
    )
  }
  return (
    <>
      {lines.map((line) => {
        const prior = priorByCategory.get(line.category)
        const delta =
          prior === undefined || prior === 0
            ? null
            : ((line.amount - prior) / prior) * 100
        return (
          <TableRow key={line.category}>
            <TableCell>
              {line.label}
              <span className="ml-2 text-xs text-muted-foreground">({line.count})</span>
            </TableCell>
            <TableCell className="whitespace-nowrap text-right tabular-nums">{formatMoney(line.amount)}</TableCell>
            <TableCell className="w-28 whitespace-nowrap text-right text-xs tabular-nums text-muted-foreground">
              {delta === null ? '—' : `${delta >= 0 ? '+' : ''}${delta.toFixed(0)}%`}
            </TableCell>
          </TableRow>
        )
      })}
    </>
  )
}

export function PnLStatement({ pnl, priorPnl, isLoading }: PnLStatementProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5" /> Profit &amp; Loss
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-3/5" />
        </CardContent>
      </Card>
    )
  }

  const priorRevenue = new Map((priorPnl?.revenue ?? []).map((l) => [l.category, l.amount]))
  const priorExpenses = new Map((priorPnl?.expenses ?? []).map((l) => [l.category, l.amount]))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Landmark className="h-5 w-5" /> Profit &amp; Loss
        </CardTitle>
        <CardDescription>
          {pnl ? `${pnl.from} → ${pnl.to} · change vs prior period` : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Line</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Δ Prior</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow className="bg-muted/50 font-medium">
              <TableCell>Income</TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums">
                {formatMoney(pnl?.total_revenue ?? 0)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
                {formatMoney(priorPnl?.total_revenue ?? 0)}
              </TableCell>
            </TableRow>
            <SectionRows lines={pnl?.revenue ?? []} priorByCategory={priorRevenue} />
            <TableRow className="bg-muted/50 font-medium">
              <TableCell>Expenses</TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums">
                {formatMoney(pnl?.total_expenses ?? 0)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
                {formatMoney(priorPnl?.total_expenses ?? 0)}
              </TableCell>
            </TableRow>
            <SectionRows lines={pnl?.expenses ?? []} priorByCategory={priorExpenses} />
          </TableBody>
          <TableFooter>
            <TableRow className="text-base font-bold">
              <TableCell>Net income</TableCell>
              <TableCell
                className={`whitespace-nowrap text-right tabular-nums ${
                  (pnl?.net_income ?? 0) < 0 ? 'text-destructive' : 'text-success'
                }`}
              >
                {formatMoney(pnl?.net_income ?? 0)}
              </TableCell>
              <TableCell className="whitespace-nowrap text-right tabular-nums text-muted-foreground">
                {formatMoney(priorPnl?.net_income ?? 0)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </CardContent>
    </Card>
  )
}
